import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link as RouterLink, useLocation, useParams, useSearchParams } from 'react-router';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import AutorenewOutlined from '@mui/icons-material/AutorenewOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';

import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import ManagePlanDialog from '@/components/organizations/plan/manage-plan-dialog';
import AddonQuotaDialog from '@/components/organizations/plan/addon-quota-dialog';
import OrganizationSubscriptionCheckoutModal from '@/components/organizations/plan/organization-subscription-checkout.modal';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useGetOrganizationQuery } from '@/state/queries/organizations/use-get-organization';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { useSubscriptionPlansQuery } from '@/state/queries/transactions/use-subscription-plans';
import {
  SUBSCRIPTION_CHECKOUT_CART_QUERY_KEY,
  useSubscriptionCheckoutCartQuery,
  type SubscriptionCheckoutCartLineDto,
  type SubscriptionCheckoutCartResponse,
} from '@/state/queries/transactions/use-subscription-checkout-cart';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { useRequestSubscriptionCheckoutNotification } from '@/state/mutations/transactions/use-request-subscription-checkout-notification';
import {
  usePatchOrganizationSubscriptionCheckoutCart,
  type PatchOrganizationSubscriptionCheckoutCartBody,
} from '@/state/mutations/transactions/use-patch-organization-subscription-checkout-cart';
import { ORGANIZATION_PERMISSIONS } from '@/enums/permissions.enum';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  collectQuotaKeysFromPlan,
  computeOrganizationRenewalAmount,
  getOveragePricePerUnit,
  getSubscriptionBasePrice,
  includedQuantityForKey,
} from '@/helpers/organization-subscription-pricing';
import type { QuotaBalanceDto } from '@/types/dtos/entitlements/quota-balance.dto';
import type { OrganizationDto } from '@/types/dtos/organizations/organization.dto';
import type {
  SubscriptionBillingPeriod,
  SubscriptionCategory,
  SubscriptionPlan,
  SubscriptionTier,
} from '@/types/dtos/transactions/subscription-plan.dto';
import {
  type CatalogProductLine,
  defaultMainSubscriptionProductKeyForCategory,
  findOrganizationInstanceForProduct,
  groupOrganizationProductLines,
  minParentTierWhenAddonParentInsufficient,
  pickDefaultBillingForAddonProductLine,
  plansForBillingWindow,
  resolveAddOnParentInstanceId,
  soleTierForProductLine,
  SUBSCRIPTION_CATALOG_CATEGORY_ORDER,
  subscriptionCategoryKey,
  subscriptionTierRank,
  uniqueTierCountForProductLine,
} from '@/helpers/organization-plan-catalog';
import { getPrimaryOrganizationSubscriptionInstance } from '@/helpers/organization-subscriptions';
import { resolveTranslatedProductField, resolveProductClaimText } from '@/helpers/resolve-translated-product-field';
import { useNotifications } from '@/hooks/use-notifications';
import { getCheckoutRecurringPriceDisplay } from '@/helpers/subscription-checkout-recurring-price-display';

const HOST_DEFAULT_PRODUCT_KEY = defaultMainSubscriptionProductKeyForCategory('HOST');

function formatMoney(amount: number): string {
  return `€${amount.toFixed(2)}`;
}

function cartHasProductKey(cartLines: SubscriptionCheckoutCartLineDto[] | undefined, productKey: string): boolean {
  const want = productKey.trim() || HOST_DEFAULT_PRODUCT_KEY;
  return (cartLines ?? []).some((l) => (l.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === want);
}

function shortQuotaSuffix(quotaKey: string): string {
  const s = String(quotaKey).trim();
  const i = s.lastIndexOf(':');
  return i >= 0 ? s.slice(i + 1).toLowerCase() : s.toLowerCase();
}

function findQuotaBalanceForProduct(
  balances: QuotaBalanceDto[] | undefined,
  subscriptionProductKey: string,
  planQuotaKey: string,
): QuotaBalanceDto | undefined {
  if (!balances?.length) {
    return undefined;
  }
  const pk = (subscriptionProductKey || HOST_DEFAULT_PRODUCT_KEY).trim();
  const raw = String(planQuotaKey).trim();
  const candidates = [raw, raw.includes(':') ? raw : `${pk}:${raw}`];
  for (const k of candidates) {
    const b = balances.find((x) => x.quotaKey === k);
    if (b) {
      return b;
    }
  }
  const suf = shortQuotaSuffix(raw);
  return balances.find((x) => shortQuotaSuffix(x.quotaKey) === suf);
}

function extraAddonUnits(
  inst: { additionalQuotaUnits?: { quotaKey: string; quantity: number }[] } | undefined,
  planQuotaKey: string,
): number {
  if (!inst?.additionalQuotaUnits?.length) {
    return 0;
  }
  const raw = String(planQuotaKey).trim();
  const suf = shortQuotaSuffix(raw);
  let m = 0;
  for (const u of inst.additionalQuotaUnits) {
    const uq = String(u.quotaKey ?? '').trim();
    if (uq === raw || uq === suf || shortQuotaSuffix(uq) === suf) {
      m = Math.max(m, Number(u.quantity) || 0);
    }
  }
  return m;
}

function quotaLabelFromAsT(
  asT: (k: string, o?: Record<string, unknown>) => string,
  productKey: string,
  planQuotaKey: string,
): string {
  const pk = productKey.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const short = shortQuotaSuffix(planQuotaKey);
  const nested = `subscription.organization.planPage.quotaKey.${pk}.${short}`;
  const r1 = asT(nested);
  if (r1 !== nested) {
    return r1;
  }
  const flat = `subscription.organization.planPage.quota.${short}`;
  const r2 = asT(flat);
  if (r2 !== flat) {
    return r2;
  }
  return planQuotaKey;
}

function sortQuotaKeysForBreakdown(keys: string[]): string[] {
  const rank = (k: string) => {
    const s = shortQuotaSuffix(k);
    if (s === 'members') {
      return 0;
    }
    if (s === 'devices') {
      return 1;
    }
    return 2;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

function mergeLinesForProduct(
  productKey: string,
  newLine: {
    subscriptionProductKey: string;
    tier?: SubscriptionTier;
    billingPeriod: 'MONTH' | 'YEAR';
    parentSubscriptionInstanceId?: string;
  },
  existing: {
    lines?: SubscriptionCheckoutCartLineDto[];
    quotaAddons?: { quotaKey: string; quantity: number }[];
  } | null,
): PatchOrganizationSubscriptionCheckoutCartBody {
  const otherLines = (existing?.lines ?? []).filter((l) => l.subscriptionProductKey !== productKey);
  const merged: NonNullable<PatchOrganizationSubscriptionCheckoutCartBody['lines']>[number] = {
    subscriptionProductKey: newLine.subscriptionProductKey,
    billingPeriod: newLine.billingPeriod,
    ...(newLine.tier !== undefined ? { tier: newLine.tier } : {}),
    ...(newLine.parentSubscriptionInstanceId
      ? { parentSubscriptionInstanceId: newLine.parentSubscriptionInstanceId }
      : {}),
  };
  return {
    lines: [...otherLines, merged] as NonNullable<PatchOrganizationSubscriptionCheckoutCartBody['lines']>,
    quotaAddons: existing?.quotaAddons ?? [],
  };
}

/** PATCH body to add add-on `line` to the cart; `null` if catalog/parent state does not allow it yet. */
function buildAddonCartMergeBody(
  org: OrganizationDto,
  line: CatalogProductLine,
  existingCart: SubscriptionCheckoutCartResponse['cart'] | null,
): PatchOrganizationSubscriptionCheckoutCartBody | null {
  const cartLines = (existingCart?.lines ?? []) as SubscriptionCheckoutCartLineDto[];
  const billing = pickDefaultBillingForAddonProductLine(line, org, cartLines);
  const windowPlans = plansForBillingWindow(line, billing);
  const first = windowPlans[0];
  if (!first) {
    return null;
  }
  const tier =
    soleTierForProductLine(line) ??
    [...windowPlans].sort((a, b) => subscriptionTierRank(b.tier) - subscriptionTierRank(a.tier))[0]?.tier;
  if (!tier) {
    return null;
  }
  if (first.requiresParent) {
    const r = resolveAddOnParentInstanceId(org, first, cartLines);
    const canAddWithParent =
      !('error' in r) &&
      (('parentSubscriptionInstanceId' in r && r.parentSubscriptionInstanceId) ||
        ('parentSatisfiedInCart' in r && r.parentSatisfiedInCart));
    if (!canAddWithParent) {
      return null;
    }
    return mergeLinesForProduct(
      line.subscriptionProductKey,
      {
        subscriptionProductKey: line.subscriptionProductKey,
        billingPeriod: billing,
        tier,
        ...('parentSubscriptionInstanceId' in r && r.parentSubscriptionInstanceId
          ? { parentSubscriptionInstanceId: r.parentSubscriptionInstanceId }
          : {}),
      },
      existingCart,
    );
  }
  return mergeLinesForProduct(
    line.subscriptionProductKey,
    {
      subscriptionProductKey: line.subscriptionProductKey,
      billingPeriod: billing,
      tier,
    },
    existingCart,
  );
}

export default function OrganizationPlanPage() {
  const { organizationId } = useParams();
  const location = useLocation();
  const t = useTranslations();
  const asT = t as (key: string, options?: Record<string, unknown>) => string;
  const activeOrganization = useActiveOrganizationStore((s) => s.activeOrganization);
  const isOrganizationRoute = location.pathname.startsWith('/organization/');
  const { data: org, isSuccess } = useGetOrganizationQuery(organizationId);
  const { data: balances } = useQuotaBalances('Organization', organizationId);
  const { data: catalog } = useSubscriptionPlansQuery({ entityType: 'Organization', enabled: true });
  const { data: permissions } = usePermissionsQuery();
  const { data: checkoutCart } = useSubscriptionCheckoutCartQuery(organizationId);
  const requestCheckoutMutation = useRequestSubscriptionCheckoutNotification();
  const patchCart = usePatchOrganizationSubscriptionCheckoutCart();
  const notification = useNotifications();
  const queryClient = useQueryClient();
  const pendingAddonAfterParentCartSaveRef = useRef<CatalogProductLine | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [subscriptionCheckoutOpen, setSubscriptionCheckoutOpen] = useState(false);
  const [manageProductKey, setManageProductKey] = useState<string | null>(null);
  /** When opening from “Switch to annual”, pre-select yearly in ManagePlanDialog (see `preferredBilling`). */
  const [managePreferredBillingOverride, setManagePreferredBillingOverride] = useState<
    SubscriptionBillingPeriod | undefined
  >(undefined);
  /** When manage opens on the parent after add-on UPGRADE was blocked: required parent tier for dialog warning. */
  const [manageDialogParentUpgradeMinTierForAddon, setManageDialogParentUpgradeMinTierForAddon] =
    useState<SubscriptionTier | null>(null);
  const [addonTarget, setAddonTarget] = useState<{ quotaKey: string; label: string } | null>(null);

  const primary = getPrimaryOrganizationSubscriptionInstance(org);
  const plan = primary?.subscriptionPlan;

  const orgPerms = organizationId ? permissions?.organizations?.[organizationId] : undefined;
  const hasLegacyOrgSubscriptionPlanPermission = !!orgPerms?.includes(
    ORGANIZATION_PERMISSIONS.UPDATE_SUBSCRIPTION_PLAN,
  );
  const canManagePlan =
    hasLegacyOrgSubscriptionPlanPermission || !!orgPerms?.includes(ORGANIZATION_PERMISSIONS.MANAGE_SUBSCRIPTION_CART);
  const canCompleteCheckout =
    hasLegacyOrgSubscriptionPlanPermission ||
    !!orgPerms?.includes(ORGANIZATION_PERMISSIONS.COMPLETE_SUBSCRIPTION_CHECKOUT);
  const canReadOrg = !!orgPerms?.includes(ORGANIZATION_PERMISSIONS.READ);

  const grouped = useMemo(() => groupOrganizationProductLines(catalog?.plans), [catalog?.plans]);
  const categories = useMemo(() => {
    return SUBSCRIPTION_CATALOG_CATEGORY_ORDER.filter((c) => grouped.has(c));
  }, [grouped]);

  const categoryTabLabels = useMemo(() => {
    const out = new Map<SubscriptionCategory, string>();
    for (const category of categories) {
      const block = grouped.get(category);
      const mainLines = block?.main ?? [];
      const categoryFallback = asT(`subscription.organization.planPage.categories.${category}`);
      if (mainLines.length === 0) {
        out.set(category, categoryFallback);
        continue;
      }
      const defaultPk = defaultMainSubscriptionProductKeyForCategory(category);
      const line = mainLines.find((l) => l.subscriptionProductKey === defaultPk) ?? mainLines[0]!;
      const pk = line.subscriptionProductKey;
      out.set(
        category,
        resolveTranslatedProductField(t, 'subscription.organization.planPage.productName', pk) ?? categoryFallback,
      );
    }
    return out;
  }, [asT, categories, grouped, t]);

  const selectedCategory = useMemo((): SubscriptionCategory | undefined => {
    const raw = searchParams.get('category');
    if (raw && (categories as readonly string[]).includes(raw)) {
      return raw as SubscriptionCategory;
    }
    return categories[0];
  }, [searchParams, categories]);

  const setSelectedCategory = useCallback(
    (_: SyntheticEvent, value: SubscriptionCategory) => {
      const next = new URLSearchParams(searchParams);
      next.set('category', value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const cartLines = useMemo(
    () => (checkoutCart?.cart?.lines ?? []) as SubscriptionCheckoutCartLineDto[],
    [checkoutCart?.cart?.lines],
  );

  const openParentManageForBlockedAddon = useCallback(
    (line: CatalogProductLine) => {
      const marker = line.plans.find((p) => p.requiresParent === true);
      if (!marker?.requiresParent) {
        notification.notifyError(asT('subscription.organization.planPage.addOnParentError'));
        return;
      }
      const minT = minParentTierWhenAddonParentInsufficient(org, line, cartLines);
      setManageDialogParentUpgradeMinTierForAddon(minT ?? null);
      setManagePreferredBillingOverride(undefined);
      pendingAddonAfterParentCartSaveRef.current = line;
      setManageProductKey(marker.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY);
    },
    [asT, cartLines, notification, org],
  );

  const handleManagePlanDialogClose = useCallback(() => {
    setManageProductKey(null);
    setManagePreferredBillingOverride(undefined);
    setManageDialogParentUpgradeMinTierForAddon(null);
    pendingAddonAfterParentCartSaveRef.current = null;
  }, []);

  const handleManagePlanCartSaved = useCallback(async () => {
    const pending = pendingAddonAfterParentCartSaveRef.current;
    pendingAddonAfterParentCartSaveRef.current = null;
    if (!pending || !org || !organizationId) {
      setSubscriptionCheckoutOpen(true);
      return;
    }
    try {
      await queryClient.refetchQueries({ queryKey: [SUBSCRIPTION_CHECKOUT_CART_QUERY_KEY, organizationId] });
      const snap = queryClient.getQueryData<SubscriptionCheckoutCartResponse>([
        SUBSCRIPTION_CHECKOUT_CART_QUERY_KEY,
        organizationId,
      ]);
      const mergeBody = buildAddonCartMergeBody(org, pending, snap?.cart ?? null);
      if (!mergeBody) {
        notification.notifyError(asT('subscription.organization.planPage.addOnParentError'));
      } else {
        await patchCart.mutateAsync({ organizationId, body: mergeBody });
      }
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setSubscriptionCheckoutOpen(true);
    }
  }, [asT, notification, org, organizationId, patchCart, queryClient]);

  const planChangeForManage = useMemo(() => {
    if (!manageProductKey) {
      return null;
    }
    const l = cartLines.find((c) => c.subscriptionProductKey === manageProductKey);
    if (!l) {
      return null;
    }
    let tier = l.tier?.trim() as SubscriptionTier | undefined;
    if (!tier) {
      for (const [, block] of grouped) {
        for (const line of [...block.main, ...block.addOn]) {
          if (line.subscriptionProductKey === manageProductKey) {
            tier = soleTierForProductLine(line);
            break;
          }
        }
        if (tier) {
          break;
        }
      }
    }
    if (!tier) {
      return null;
    }
    return {
      tier,
      billingPeriod: l.billingPeriod as SubscriptionBillingPeriod | undefined,
      additionalQuotaUnits: l.additionalQuotaUnits,
    };
  }, [cartLines, manageProductKey, grouped]);

  const instanceForManage = useMemo(
    () => (manageProductKey && org ? findOrganizationInstanceForProduct(org, manageProductKey) : undefined),
    [org, manageProductKey],
  );
  const manageCurrentTier = (instanceForManage?.tier ??
    instanceForManage?.subscriptionPlan?.tier ??
    'FREE') as SubscriptionTier;
  const manageCurrentBilling = (instanceForManage?.billingPeriod ??
    instanceForManage?.subscriptionPlan?.billingPeriod ??
    'MONTH') as SubscriptionBillingPeriod;

  const preferredBillingForManage = useMemo(() => {
    if (!manageProductKey) {
      return undefined;
    }
    for (const [, block] of grouped) {
      for (const line of [...block.main, ...block.addOn]) {
        if (line.subscriptionProductKey === manageProductKey) {
          return pickDefaultBillingForAddonProductLine(line, org, cartLines);
        }
      }
    }
    return undefined;
  }, [cartLines, grouped, manageProductKey, org]);

  const managePlanDialogKey = useMemo(
    () =>
      manageProductKey
        ? `plan-manage-${organizationId}-${manageProductKey}-${managePreferredBillingOverride ?? 'default'}-${JSON.stringify(cartLines.map((c) => c.subscriptionProductKey))}`
        : 'plan-manage-closed',
    [cartLines, managePreferredBillingOverride, manageProductKey, organizationId],
  );

  useEffect(() => {
    if (searchParams.get('subscriptionCheckout') === '1') {
      setSubscriptionCheckoutOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('subscriptionCheckout');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const manageProduct = searchParams.get('manageProduct')?.trim();
    if (!manageProduct || !isSuccess || !org) {
      return;
    }
    setManageProductKey(manageProduct);
    setManagePreferredBillingOverride(undefined);
    setManageDialogParentUpgradeMinTierForAddon(null);
    const next = new URLSearchParams(searchParams);
    next.delete('manageProduct');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isSuccess, org]);

  const addProductToCart = useCallback(
    async (params: {
      productKey: string;
      line: { tier?: SubscriptionTier; billing: 'MONTH' | 'YEAR'; parentId?: string };
    }) => {
      if (!organizationId) {
        return;
      }
      const merged = mergeLinesForProduct(
        params.productKey,
        {
          subscriptionProductKey: params.productKey,
          ...(params.line.tier !== undefined ? { tier: params.line.tier } : {}),
          billingPeriod: params.line.billing,
          ...(params.line.parentId ? { parentSubscriptionInstanceId: params.line.parentId } : {}),
        },
        checkoutCart?.cart ?? null,
      );
      await patchCart.mutateAsync({ organizationId, body: merged });
      if (!canCompleteCheckout) {
        notification.notifySuccess(asT('subscription.organization.planPage.cartSavedToast'));
      }
    },
    [asT, canCompleteCheckout, checkoutCart?.cart, notification, organizationId, patchCart],
  );

  const handleAddDirect = useCallback(
    async (line: CatalogProductLine) => {
      if (!org || !organizationId) {
        return;
      }
      const billing = pickDefaultBillingForAddonProductLine(line, org, cartLines);
      const windowPlans = plansForBillingWindow(line, billing);
      if (windowPlans.length === 0) {
        return;
      }
      const first = windowPlans[0]!;
      if (first.requiresParent) {
        const r = resolveAddOnParentInstanceId(org, first, cartLines);
        const canAddWithParent =
          !('error' in r) &&
          (('parentSubscriptionInstanceId' in r && r.parentSubscriptionInstanceId) ||
            ('parentSatisfiedInCart' in r && r.parentSatisfiedInCart));
        if (canAddWithParent) {
          if (uniqueTierCountForProductLine(line) === 1) {
            await addProductToCart({
              productKey: line.subscriptionProductKey,
              line: {
                billing,
                ...('parentSubscriptionInstanceId' in r && r.parentSubscriptionInstanceId
                  ? { parentId: r.parentSubscriptionInstanceId }
                  : {}),
              },
            });
            return;
          }
        } else {
          openParentManageForBlockedAddon(line);
        }
        return;
      }
      if (uniqueTierCountForProductLine(line) !== 1) {
        return;
      }
      await addProductToCart({ productKey: line.subscriptionProductKey, line: { billing } });
    },
    [addProductToCart, asT, cartLines, openParentManageForBlockedAddon, org, organizationId],
  );

  const tryOpenManageDialog = useCallback(
    (line: CatalogProductLine, options?: { preferredBilling?: SubscriptionBillingPeriod }) => {
      if (!org) {
        return;
      }
      setManageDialogParentUpgradeMinTierForAddon(null);
      setManagePreferredBillingOverride(options?.preferredBilling);
      const b = pickDefaultBillingForAddonProductLine(line, org, cartLines);
      const first = plansForBillingWindow(line, b)[0] ?? line.plans[0];
      if (first?.requiresParent) {
        const r = resolveAddOnParentInstanceId(org, first, cartLines);
        if ('error' in r) {
          openParentManageForBlockedAddon(line);
          return;
        }
        if (
          !('parentSubscriptionInstanceId' in r && r.parentSubscriptionInstanceId) &&
          !('parentSatisfiedInCart' in r && r.parentSatisfiedInCart)
        ) {
          openParentManageForBlockedAddon(line);
          return;
        }
      }
      setManageProductKey(line.subscriptionProductKey);
    },
    [cartLines, openParentManageForBlockedAddon, org],
  );

  const addAddonToCartThenOpenCheckout = useCallback(
    async (line: CatalogProductLine) => {
      if (!org || !organizationId) {
        return;
      }
      if (cartHasProductKey(cartLines, line.subscriptionProductKey)) {
        setSubscriptionCheckoutOpen(true);
        return;
      }
      const mergeBody = buildAddonCartMergeBody(org, line, checkoutCart?.cart ?? null);
      if (!mergeBody) {
        const billing = pickDefaultBillingForAddonProductLine(line, org, cartLines);
        const windowPlans = plansForBillingWindow(line, billing);
        const first = windowPlans[0];
        if (!first) {
          notification.notifyError(asT('subscription.organization.planPage.changeModalNoPlans'));
          return;
        }
        const tier =
          soleTierForProductLine(line) ??
          [...windowPlans].sort((a, b) => subscriptionTierRank(b.tier) - subscriptionTierRank(a.tier))[0]?.tier;
        if (!tier) {
          tryOpenManageDialog(line);
          return;
        }
        if (first.requiresParent) {
          openParentManageForBlockedAddon(line);
        }
        return;
      }
      try {
        await patchCart.mutateAsync({ organizationId, body: mergeBody });
        if (!canCompleteCheckout) {
          notification.notifySuccess(asT('subscription.organization.planPage.cartSavedToast'));
        }
        setSubscriptionCheckoutOpen(true);
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Request failed');
      }
    },
    [
      asT,
      canCompleteCheckout,
      cartLines,
      checkoutCart?.cart,
      notification,
      openParentManageForBlockedAddon,
      org,
      organizationId,
      patchCart,
      tryOpenManageDialog,
    ],
  );

  const cartTotalEur = checkoutCart?.totals?.totalEur ?? 0;
  const hasPendingCheckoutCart = cartTotalEur > 0;

  const plansLabel = t('subscription.organization.planPage.pageTitle' as const);

  const headerCartDesktop = (
    <Box
      sx={{
        display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
        alignItems: 'center',
        gap: 1.5,
        ml: 'auto',
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      {hasPendingCheckoutCart ? (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {t('subscription.organization.planPage.headerCartTeaser' as const, {
            amount: formatMoney(cartTotalEur),
          })}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {t('subscription.organization.planPage.headerCartEmpty' as const)}
        </Typography>
      )}
      {hasPendingCheckoutCart && canCompleteCheckout ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ShoppingCartOutlined />}
          onClick={() => setSubscriptionCheckoutOpen(true)}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('subscription.organization.planPage.checkout' as const)}
        </Button>
      ) : null}
      {hasPendingCheckoutCart && !canCompleteCheckout && canReadOrg ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ShoppingCartOutlined />}
          disabled={requestCheckoutMutation.isPending}
          onClick={async () => {
            if (organizationId) {
              await requestCheckoutMutation.mutateAsync({ organizationId });
            }
          }}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('subscription.organization.planPage.requestCheckout' as const)}
        </Button>
      ) : null}
    </Box>
  );

  const headerCartMobile = (
    <Box
      sx={{
        display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' },
        gap: 0.5,
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <IconButton
        onClick={() => setSubscriptionCheckoutOpen(true)}
        color={hasPendingCheckoutCart ? 'primary' : 'default'}
        sx={{
          ml: { xs: 0.5, sm: 1 },
          ...(!hasPendingCheckoutCart ? { color: 'text.secondary' } : {}),
        }}
        aria-label={t('subscription.organization.planPage.checkout' as const)}
      >
        <ShoppingCartOutlined />
      </IconButton>
    </Box>
  );

  const breadcrumbBox = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: { xs: 'none', sm: '300px', md: '500px' },
        minWidth: 0,
        flex: { xs: '1 1 auto', sm: '0 1 auto' },
      }}
    >
      {isOrganizationRoute && activeOrganization && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexShrink: 1,
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
          }}
        >
          {activeOrganization.name}
        </Typography>
      )}
      <Typography
        variant="h6"
        sx={{
          fontWeight: isOrganizationRoute ? 400 : 600,
          color: isOrganizationRoute ? 'text.secondary' : 'text.primary',
          flexShrink: 0,
          ml: isOrganizationRoute ? 1 : 0,
          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
        }}
      >
        {isOrganizationRoute && activeOrganization ? '/ ' : ''}
        {plansLabel}
      </Typography>
    </Box>
  );

  if (!organizationId || !activeOrganization || organizationId !== activeOrganization.id) {
    return null;
  }

  if (!isSuccess || !org) {
    return (
      <OrganizationContentStack
        content={
          <Stack
            width="100%"
            spacing={0}
            pt={0}
            pb={1}
            bgcolor="background.default"
            sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <PageHeaderBar
              mainContent={
                <>
                  {breadcrumbBox}
                  {headerCartDesktop}
                  {headerCartMobile}
                </>
              }
            />
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
              <Typography color="text.secondary">{t('common.loading')}</Typography>
            </Box>
          </Stack>
        }
      />
    );
  }

  const primaryPlanCategory = plan ? subscriptionCategoryKey(plan) : undefined;
  const showMeteredQuotaCard =
    Boolean(plan?.overagePrices?.length) && selectedCategory !== undefined && selectedCategory === primaryPlanCategory;

  const section = selectedCategory ? grouped.get(selectedCategory) : undefined;

  return (
    <OrganizationContentStack
      content={
        <Stack
          width="100%"
          spacing={0}
          pt={0}
          pb={1}
          bgcolor="background.default"
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <PageHeaderBar
            mainContent={
              <>
                {breadcrumbBox}
                {headerCartDesktop}
                {headerCartMobile}
              </>
            }
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              px: { xs: 1, sm: 2 },
              pt: { xs: 1, sm: 2 },
              pb: 4,
            }}
          >
            <Stack spacing={2} sx={{ maxWidth: 960 }}>
              {categories.length > 0 ? (
                <>
                  <Tabs
                    value={selectedCategory ?? categories[0]!}
                    onChange={setSelectedCategory}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label={t('subscription.organization.planPage.categoryTabsAria' as const)}
                    sx={{
                      'borderBottom': 1,
                      'borderColor': 'divider',
                      'minHeight': 42,
                      '& .MuiTab-root': { minHeight: 42, textTransform: 'none' },
                    }}
                  >
                    {categories.map((category) => (
                      <Tab
                        key={category}
                        value={category}
                        label={
                          categoryTabLabels.get(category) ??
                          asT(`subscription.organization.planPage.categories.${category}`)
                        }
                      />
                    ))}
                  </Tabs>

                  {section ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                      {section.main.length > 0 ? (
                        <Stack spacing={2}>
                          {section.main.map((line) => {
                            return (
                              <ProductLineBlock
                                key={`${selectedCategory}-main-${line.subscriptionProductKey}`}
                                line={line}
                                org={org}
                                asT={asT}
                                t={t}
                                canManagePlan={!!canManagePlan}
                                onAddDirect={() => void handleAddDirect(line)}
                                onSelectModal={() => tryOpenManageDialog(line)}
                                onManageModal={() => tryOpenManageDialog(line)}
                                onSwitchToAnnual={() => tryOpenManageDialog(line, { preferredBilling: 'YEAR' })}
                                quotaBalances={balances}
                                checkoutCartLines={cartLines}
                                orgLinkTeam={
                                  line.subscriptionProductKey === HOST_DEFAULT_PRODUCT_KEY ? (
                                    <Link
                                      component={RouterLink}
                                      to={`/organization/${organizationId}/team`}
                                      underline="hover"
                                    >
                                      {asT('subscription.organization.planPage.viewTeam')}
                                    </Link>
                                  ) : null
                                }
                              />
                            );
                          })}
                        </Stack>
                      ) : null}
                      {section.addOn.length > 0 ? (
                        <Stack spacing={2} sx={{ mt: section.main.length ? 2 : 0 }}>
                          <Stack spacing={0.5}>
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                              {asT('subscription.organization.planPage.addOnsTitle')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {asT('subscription.organization.planPage.addOnsSubtitle')}
                            </Typography>
                          </Stack>
                          {section.addOn.map((line) => {
                            return (
                              <ProductLineBlock
                                key={`${selectedCategory}-add-${line.subscriptionProductKey}`}
                                line={line}
                                org={org}
                                asT={asT}
                                t={t}
                                canManagePlan={!!canManagePlan}
                                onAddDirect={() => void handleAddDirect(line)}
                                onSelectModal={() => tryOpenManageDialog(line)}
                                onManageModal={() => tryOpenManageDialog(line)}
                                quotaBalances={balances}
                                checkoutCartLines={cartLines}
                                orgLinkTeam={null}
                                inCheckoutCart={cartHasProductKey(cartLines, line.subscriptionProductKey)}
                                onAddonCheckout={() => void addAddonToCartThenOpenCheckout(line)}
                                cartMutationPending={patchCart.isPending}
                              />
                            );
                          })}
                        </Stack>
                      ) : null}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {asT('subscription.organization.planPage.noCategoryContent')}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {asT('subscription.organization.planPage.noCatalogCategories')}
                </Typography>
              )}

              {showMeteredQuotaCard && plan?.overagePrices?.length ? (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                      <Typography variant="h6" component="h2">
                        {asT('subscription.organization.planPage.addOnsTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {asT('subscription.organization.planPage.addOnsSubtitle')}
                      </Typography>
                    </Stack>
                    <Stack divider={<Divider />} spacing={0}>
                      {plan.overagePrices.map((row: NonNullable<SubscriptionPlan['overagePrices']>[number]) => (
                        <Stack
                          key={row.quotaKey}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          sx={{ py: 2 }}
                        >
                          <Stack spacing={0.5}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="subtitle1">
                                {row.quotaKey === 'members'
                                  ? asT('subscription.organization.planPage.quota.members')
                                  : row.quotaKey === 'devices'
                                    ? asT('subscription.organization.planPage.quota.devices')
                                    : row.quotaKey}
                              </Typography>
                              <Chip
                                size="small"
                                label={asT('subscription.organization.planPage.addOnBadge')}
                                color="primary"
                                variant="outlined"
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {asT('subscription.organization.planPage.addOnPricePerPeriod', {
                                price: row.pricePerUnit.toFixed(2),
                              })}
                            </Typography>
                          </Stack>
                          <Button
                            variant="text"
                            disabled={!canManagePlan}
                            onClick={() =>
                              setAddonTarget({
                                quotaKey: row.quotaKey,
                                label:
                                  row.quotaKey === 'members'
                                    ? asT('subscription.organization.planPage.quota.members')
                                    : row.quotaKey,
                              })
                            }
                          >
                            {asT('subscription.organization.planPage.addMore')}
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}
            </Stack>
          </Box>

          {organizationId && manageProductKey ? (
            <ManagePlanDialog
              key={managePlanDialogKey}
              open
              onClose={handleManagePlanDialogClose}
              organizationId={organizationId}
              currentTier={manageCurrentTier}
              currentBillingPeriod={manageCurrentBilling}
              preferredBilling={managePreferredBillingOverride ?? preferredBillingForManage}
              canCompleteCheckout={!!canCompleteCheckout}
              embeddedInCheckout={false}
              checkoutCartPlanChange={planChangeForManage}
              checkoutCartLines={cartLines}
              editingSubscriptionProductKey={manageProductKey}
              subscriptionProductKeyFilter={manageProductKey}
              checkoutQuotaAddons={checkoutCart?.cart?.quotaAddons}
              onCartSaved={() => void handleManagePlanCartSaved()}
              parentUpgradeRequiredForAddonMinTier={manageDialogParentUpgradeMinTierForAddon ?? undefined}
            />
          ) : null}

          {addonTarget ? (
            <AddonQuotaDialog
              open
              onClose={() => setAddonTarget(null)}
              organizationId={organizationId}
              quotaKey={addonTarget.quotaKey}
              quotaLabel={addonTarget.label}
              canCompleteCheckout={canCompleteCheckout}
            />
          ) : null}

          <OrganizationSubscriptionCheckoutModal
            open={subscriptionCheckoutOpen}
            onClose={() => setSubscriptionCheckoutOpen(false)}
            organizationId={organizationId}
          />
        </Stack>
      }
    />
  );
}

function AddonCheckoutStylePriceRow({
  t,
  plan,
  billing,
  cadenceLabel,
}: {
  t: ReturnType<typeof useTranslations>;
  plan: SubscriptionPlan;
  billing: SubscriptionBillingPeriod;
  cadenceLabel: string;
}) {
  const d = getCheckoutRecurringPriceDisplay((key) => t(key as TranslationKey), plan, billing, undefined);
  return (
    <Stack spacing={0.25} sx={{ flex: '0 1 auto', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {cadenceLabel}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.25 }}>
        <Typography component="span" variant="body1" fontWeight={600}>
          {d.amountLabel}
        </Typography>
        {d.recurringDisplayEur !== undefined ? (
          <>
            <Typography component="span" variant="body2" color="text.secondary">
              {d.periodMid}
            </Typography>
            {d.periodTail ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {d.periodTail}
              </Typography>
            ) : null}
          </>
        ) : null}
      </Box>
    </Stack>
  );
}

type ProductBlockProps = {
  line: CatalogProductLine;
  org: OrganizationDto;
  asT: (k: string, o?: Record<string, unknown>) => string;
  t: ReturnType<typeof useTranslations>;
  canManagePlan: boolean;
  onAddDirect: () => void;
  onSelectModal: () => void;
  onManageModal: () => void;
  /** Main line: open manage dialog with annual billing pre-selected (e.g. “Switch to annual”). */
  onSwitchToAnnual?: () => void;
  quotaBalances: QuotaBalanceDto[] | undefined;
  orgLinkTeam: ReactNode;
  /** Add-on: this product key is already on the org checkout cart. */
  inCheckoutCart?: boolean;
  /** Add-on: add line to cart (if needed) then open subscription checkout modal. */
  onAddonCheckout?: () => void | Promise<void>;
  cartMutationPending?: boolean;
  /** Draft checkout lines (parent yearly in cart → add-on defaults to yearly). */
  checkoutCartLines?: SubscriptionCheckoutCartLineDto[];
};

function ProductLineBlock(p: ProductBlockProps) {
  const {
    line,
    asT,
    t,
    canManagePlan,
    onAddDirect,
    onSelectModal,
    onManageModal,
    onSwitchToAnnual,
    quotaBalances,
    orgLinkTeam,
    inCheckoutCart,
    onAddonCheckout,
    cartMutationPending,
    checkoutCartLines,
  } = p;
  const org = p.org;
  const tierCount = uniqueTierCountForProductLine(line);
  const inst = findOrganizationInstanceForProduct(org, line.subscriptionProductKey);
  const isBooked = Boolean(inst);
  const plan = inst?.subscriptionPlan;
  const renewalAmount = !inst ? 0 : computeOrganizationRenewalAmount(inst.subscriptionPlan, inst.additionalQuotaUnits);
  const planBilling = (plan?.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod;
  const billingLabel =
    planBilling === 'YEAR'
      ? asT('subscription.organization.planPage.billingAnnual')
      : asT('subscription.organization.planPage.billingMonthly');
  const productI18n = `subscription.organization.planPage.productName.${line.subscriptionProductKey}`;
  const nameTry = asT(productI18n);
  const displayName = nameTry === productI18n ? line.subscriptionProductKey : nameTry;

  const sectionTitleTier = (() => {
    if (isBooked && plan) {
      const tierKey = String(plan.tier ?? inst?.tier ?? 'FREE');
      return asT(`subscription.organization.tiers.${tierKey}` as 'subscription.organization.tiers.FREE');
    }
    const billing = pickDefaultBillingForAddonProductLine(line, org, checkoutCartLines ?? []);
    const p = plansForBillingWindow(line, billing)[0];
    const tierKey = String(p?.tier ?? 'FREE');
    return asT(`subscription.organization.tiers.${tierKey}` as 'subscription.organization.tiers.FREE');
  })();

  const membersBal = useMemo(
    () => findQuotaBalanceForProduct(quotaBalances, line.subscriptionProductKey, 'members'),
    [quotaBalances, line.subscriptionProductKey],
  );

  const breakdownQuotaKeys = !plan ? ([] as string[]) : sortQuotaKeysForBreakdown(collectQuotaKeysFromPlan(plan));

  const seatProgress = useMemo(() => {
    if (!membersBal || membersBal.totalGranted <= 0) {
      return 0;
    }
    return Math.min(100, (membersBal.totalConsumed / membersBal.totalGranted) * 100);
  }, [membersBal]);

  const addonPreviewTier = useMemo(() => {
    if (!line.isAddon) {
      return undefined;
    }
    const billing = pickDefaultBillingForAddonProductLine(line, org, checkoutCartLines ?? []);
    const windowPlans = plansForBillingWindow(line, billing);
    return (
      soleTierForProductLine(line) ??
      [...windowPlans].sort((a, b) => subscriptionTierRank(b.tier) - subscriptionTierRank(a.tier))[0]?.tier
    );
  }, [checkoutCartLines, line, org]);

  const addonClaim = useMemo(
    () => (line.isAddon ? resolveProductClaimText(t, line.subscriptionProductKey, addonPreviewTier) : undefined),
    [line.isAddon, line.subscriptionProductKey, addonPreviewTier, t],
  );

  const addonMonthPlan = useMemo(() => {
    if (!line.isAddon || !addonPreviewTier) {
      return undefined;
    }
    return plansForBillingWindow(line, 'MONTH').find((p) => p.tier === addonPreviewTier);
  }, [addonPreviewTier, line]);

  const addonYearPlan = useMemo(() => {
    if (!line.isAddon || !addonPreviewTier) {
      return undefined;
    }
    return plansForBillingWindow(line, 'YEAR').find((p) => p.tier === addonPreviewTier);
  }, [addonPreviewTier, line]);

  const addonMinParentTierWhenInsufficient = useMemo(
    () => minParentTierWhenAddonParentInsufficient(org, line, checkoutCartLines ?? []),
    [checkoutCartLines, line, org],
  );

  const hasAnnualCatalogOption = line.plans.some((pl) => (pl.billingPeriod ?? 'MONTH') === 'YEAR');

  if (line.isAddon) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'flex-start' }}
            justifyContent="space-between"
          >
            <Stack spacing={1} flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                  {displayName}
                </Typography>
                <Chip
                  size="small"
                  label={asT('subscription.organization.planPage.addOnBadge')}
                  color="primary"
                  variant="outlined"
                />
                {inCheckoutCart ? (
                  <Chip
                    size="small"
                    label={asT('subscription.organization.planPage.addonInCheckoutCart')}
                    color="success"
                    variant="outlined"
                  />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
                {addonClaim ?? asT('subscription.organization.planPage.addOnLineHint')}
                {addonMinParentTierWhenInsufficient ? (
                  <>
                    {' '}
                    {asT('subscription.organization.planPage.addonRequiresParentTierHint', {
                      tier: asT(
                        `subscription.organization.tiers.${addonMinParentTierWhenInsufficient}` as 'subscription.organization.tiers.FREE',
                      ),
                    })}
                  </>
                ) : null}
              </Typography>
              {addonMonthPlan || addonYearPlan ? (
                <Box
                  sx={{
                    pt: 0.25,
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    columnGap: 3,
                    rowGap: 1.5,
                  }}
                >
                  {addonMonthPlan ? (
                    <AddonCheckoutStylePriceRow
                      t={t}
                      plan={addonMonthPlan}
                      billing="MONTH"
                      cadenceLabel={asT('subscription.organization.planPage.billingMonthly')}
                    />
                  ) : null}
                  {addonMonthPlan && addonYearPlan ? (
                    <Box
                      aria-hidden
                      sx={{
                        alignSelf: 'stretch',
                        width: '1px',
                        flexShrink: 0,
                        bgcolor: 'divider',
                        display: { xs: 'none', sm: 'block' },
                      }}
                    />
                  ) : null}
                  {addonYearPlan ? (
                    <AddonCheckoutStylePriceRow
                      t={t}
                      plan={addonYearPlan}
                      billing="YEAR"
                      cadenceLabel={asT('subscription.organization.planPage.billingAnnual')}
                    />
                  ) : null}
                </Box>
              ) : null}
            </Stack>
            {canManagePlan ? (
              <Button
                variant="text"
                color="primary"
                disabled={!!cartMutationPending}
                onClick={() => {
                  if (isBooked) {
                    onManageModal();
                    return;
                  }
                  void onAddonCheckout?.();
                }}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0, fontWeight: 600 }}
              >
                {isBooked
                  ? asT('subscription.organization.planPage.managePlan')
                  : inCheckoutCart
                    ? asT('subscription.organization.planPage.addonOpenCheckout')
                    : t('common.upgrade')}
              </Button>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'background.paper' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Stack spacing={0.75}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {sectionTitleTier}
              </Typography>
              {isBooked && plan && renewalAmount > 0 ? (
                <Chip size="small" label={billingLabel} variant="outlined" />
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {asT('subscription.organization.planPage.planScopeHint')}
            </Typography>
          </Stack>

          {isBooked && plan ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack spacing={1.5} flex={1} minWidth={0}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatMoney(renewalAmount)}
                  </Typography>
                  {membersBal && (membersBal.totalGranted > 0 || includedQuantityForKey(plan, 'members') > 0) ? (
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {asT('subscription.organization.planPage.seatsUsed', {
                          used: membersBal.totalConsumed,
                          total: membersBal.totalGranted,
                        })}
                      </Typography>
                      <LinearProgress variant="determinate" value={seatProgress} sx={{ height: 6, borderRadius: 1 }} />
                    </Stack>
                  ) : null}
                </Stack>
                {canManagePlan ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={onManageModal}
                    sx={{ flexShrink: 0 }}
                  >
                    {asT('subscription.organization.planPage.managePlan')}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                gap={2}
              >
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {asT('subscription.organization.planPage.notBookedHint')}
                  </Typography>
                  <Typography variant="h5" color="text.secondary">
                    {formatMoney(0)}
                  </Typography>
                </Stack>
                {canManagePlan ? (
                  <Button variant="contained" color="primary" onClick={tierCount > 1 ? onSelectModal : onAddDirect}>
                    {asT('subscription.organization.planPage.add')}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          )}

          {isBooked && plan && breakdownQuotaKeys.length > 0 ? (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 520 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      {asT('subscription.organization.planPage.planBreakdownProductPlanRow', {
                        productName: displayName,
                      })}
                    </TableCell>
                    <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2">{sectionTitleTier}</Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>{'\u00a0'}</TableCell>
                    <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider', fontWeight: 500 }}>
                      {formatMoney(getSubscriptionBasePrice(plan))}
                    </TableCell>
                  </TableRow>
                  {breakdownQuotaKeys.map((qk) => {
                    const bal = findQuotaBalanceForProduct(quotaBalances, line.subscriptionProductKey, qk);
                    const used = bal?.totalConsumed ?? 0;
                    const included = includedQuantityForKey(plan, qk);
                    const extra = inst ? extraAddonUnits(inst, qk) : 0;
                    const unit = getOveragePricePerUnit(plan, qk);
                    const addonCost = extra > 0 && unit !== undefined ? extra * unit : 0;
                    const isMembers = shortQuotaSuffix(qk) === 'members';
                    return (
                      <TableRow key={qk}>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          {quotaLabelFromAsT(asT, line.subscriptionProductKey, qk)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2">{used}</Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {asT('subscription.organization.planPage.planBreakdownIncludedInRow', { count: included })}
                          </Typography>
                          {isMembers && orgLinkTeam ? <> {orgLinkTeam}</> : null}
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider', fontWeight: 500 }}>
                          {formatMoney(addonCost)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          {isBooked &&
          plan &&
          planBilling === 'MONTH' &&
          hasAnnualCatalogOption &&
          canManagePlan &&
          renewalAmount > 0 &&
          onSwitchToAnnual ? (
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<AutorenewOutlined fontSize="small" />}
              onClick={() => onSwitchToAnnual()}
              sx={{ alignSelf: 'flex-end', fontWeight: 600 }}
            >
              {asT('subscription.organization.planPage.switchToAnnual')}
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
